import { ChangeEvent, useCallback, useEffect, useRef, useState } from 'react'
import lowerCase from 'lodash/lowerCase'
import upperCase from 'lodash/upperCase'
import { Button } from '@/atoms/Button'
import { CaretRightIcon } from '@/atoms/Icons/CaretRightIcon'
import { MagnifyingGlassIcon } from '@/atoms/Icons/MagnifyingGlassIcon'
import { PhotonInput } from '@/atoms/PhotonInput'
import { ParagraphSM, Text } from '@/atoms/Text'
import { GuildProject, GuildProjectAsset, Maybe } from '@/types/codegen-federation'
import { useSafeRouter } from '@/utils/nextUtils/safe-router'
import { useTranslate } from '@/utils/translate/translate-client'
import { useFilmmakerDashboard } from './context/FilmmakerDashboardContext'

export const ProjectComboSelector = () => {
  const { projects } = useFilmmakerDashboard()
  const [options, setOptions] = useState<(GuildProject | GuildProjectAsset)[]>([])
  const [name, setName] = useState<string>()
  const searchListModalRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchListModalRef.current && !searchListModalRef.current.contains(event.target as Node)) {
        setOptions([])
        setName('')
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  const handleNameChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      setName(e.target.value)
      if (e.target.value === '') {
        setOptions([])
        return
      }

      const matchingProjects = projects?.filter((p) => lowerCase(p.name as string)?.includes(lowerCase(e.target.value)))
      let allAssets: Maybe<GuildProjectAsset>[] = []
      projects?.forEach((p) => {
        allAssets = [...allAssets, ...(p.projectAssets ? p.projectAssets : [])]
      })
      const matchingAssets = allAssets.filter((a) => {
        return (
          lowerCase(a?.title as string)?.includes(lowerCase(e.target.value)) ||
          lowerCase(a?.alias as string)?.includes(lowerCase(e.target.value))
        )
      })

      setOptions([...(matchingProjects as GuildProject[]), ...(matchingAssets as GuildProjectAsset[])])
    },
    [setName, projects, setOptions],
  )

  const handleBlur = useCallback(() => {
    setTimeout(() => {
      setOptions([])
      setName('')
    }, 200)
  }, [setOptions, setName])

  return (
    <div className="relative z-10 flex w-full items-center gap-2 lg:items-center">
      <div className="flex min-w-[300px] items-center gap-1 rounded-lg border border-gray-300 bg-white px-2 py-1">
        <MagnifyingGlassIcon size={16} color="gray-600" />
        <PhotonInput
          value={name}
          placeholder="Search"
          className="!m-1 !border-0 !p-1 font-medium"
          containerClassName="!w-full"
          onChange={handleNameChange}
        />
      </div>
      {options && options.length > 0 && (
        <div
          ref={searchListModalRef}
          className="absolute top-14 flex max-h-60 w-full flex-col overflow-auto rounded-md bg-white p-2 text-base shadow-lg"
          onClick={handleBlur}
        >
          {options.map((option) => {
            return (
              <div
                key={`option_${option.id}`}
                className="border-b border-b-gray-200 py-1 first:pt-0 last:border-b-transparent last:pb-0"
              >
                {option.__typename === 'GuildProject' && <GuildProjectSelectOption project={option} />}
                {option.__typename === 'GuildProjectAsset' && <GuildProjectAssetSelectOption asset={option} />}
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

interface ProjectOptionProps {
  project: GuildProject
}
const GuildProjectSelectOption = ({ project }: ProjectOptionProps) => {
  const router = useSafeRouter()
  const { t } = useTranslate('guild-create')

  const handleTap = useCallback(() => {
    router.push(`/guild/create/projects/${project?.id}`)
  }, [router, project])

  return (
    <Button variant="plain" className="flex w-full items-center justify-between" onClick={handleTap}>
      <div className="flex flex-col items-start">
        <Text color="gray-400" className="-mb-2 text-[10px]">
          {upperCase(t('project', 'Project'))}
        </Text>
        <ParagraphSM className="pl-2">{project?.name}</ParagraphSM>
      </div>
      <CaretRightIcon size={16} color="gray-400" />
    </Button>
  )
}

interface AssetOptionProps {
  asset: GuildProjectAsset
}
const GuildProjectAssetSelectOption = ({ asset }: AssetOptionProps) => {
  const router = useSafeRouter()
  const { t } = useTranslate('guild-create')
  const { projects } = useFilmmakerDashboard()

  const handleTap = useCallback(() => {
    const project = projects?.find((p) => p.projectAssets?.find((a) => a?.id === asset.id))
    router.push(`/guild/create/projects/${project?.id}/version/${asset?.id}`)
  }, [router, asset, projects])

  return (
    <Button variant="plain" className="flex w-full items-center justify-between" onClick={handleTap}>
      <div className="flex flex-col items-start">
        <Text color="gray-400" className="-mb-2 text-[10px]">
          {upperCase(t('version', 'Version'))}
        </Text>
        <ParagraphSM className="pl-2">{asset?.title ?? asset?.alias ?? 'No title'}</ParagraphSM>
      </div>
      <CaretRightIcon size={16} color="gray-400" />
    </Button>
  )
}
